/* Hallmark · component-discipline · panel-system
 * Dual Power Feed (A/B) Redundancy Calculator & Failover Verification.
 * Standard Electrical Engineering Compliance: NEC / NFPA 70 80% Continuous Load Derating.
 * Zero raw emojis, crisp monospace typography, token-based styling.
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  Zap,
  Server,
  BarChart2,
  ShieldCheck,
  AlertTriangle,
  AlertOctagon,
  Sliders,
  PowerOff,
} from 'lucide-react';
import type { RackEquipmentItem } from '../../hooks/useRackWorkspaceData';
import {
  calculateRackPowerRedundancy,
  DEFAULT_VOLTAGE_V,
  DEFAULT_AMPERAGE_A,
  type FeedState,
} from '../../utils/rackPowerRedundancy.logic';

interface PduSummaryItem {
  equipment_id: string | number;
  name?: string;
  total_outlets?: number;
  free_outlets?: number;
  load_pct?: number;
}

interface RackPowerTabProps {
  equipmentList: RackEquipmentItem[];
  maxPower: number;
  powerSummary?: {
    rack_id?: number | string;
    pdus?: PduSummaryItem[];
  } | null;
}

/* ── Section label ──────────────────────────────────────────────────────── */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-[9px] uppercase tracking-[0.12em] font-semibold"
      style={{ color: 'var(--text-muted)' }}
    >
      {children}
    </p>
  );
}

/* ── Feed Power Meter with 80% Safety Threshold Line ─────────────────────── */
interface FeedMeterProps {
  feed: FeedState;
  oppositeFeedName: string;
  isSimulatedFailure: boolean;
  failoverSurgeActive: boolean;
}

function FeedMeter({
  feed,
  oppositeFeedName,
  isSimulatedFailure,
  failoverSurgeActive,
}: FeedMeterProps) {
  const { capacity } = feed;

  // Values depending on simulation state
  const activeDrawW = isSimulatedFailure
    ? 0
    : failoverSurgeActive
    ? feed.failoverDrawW
    : feed.normalDrawW;

  const activePct = isSimulatedFailure
    ? 0
    : Math.min(Math.round((activeDrawW / capacity.ratedPowerW) * 100), 120);

  const activeCurrentA = isSimulatedFailure
    ? 0
    : Math.round((activeDrawW / capacity.voltageV) * 10) / 10;

  const failoverPct = Math.min(Math.round((feed.failoverDrawW / capacity.ratedPowerW) * 100), 120);

  // Fill color
  const isTrip = activeDrawW > capacity.ratedPowerW;
  const isOverSafe = activeDrawW > capacity.safeLimitW;

  const barColor = isSimulatedFailure
    ? 'var(--text-muted)'
    : isTrip
    ? 'var(--status-critical)'
    : isOverSafe
    ? 'var(--status-warning)'
    : 'var(--status-normal)';

  return (
    <div
      className="flex-1 rounded-sm p-3 flex flex-col justify-between transition-colors"
      style={{
        background: isSimulatedFailure ? 'var(--bg-panel)' : 'var(--bg-app)',
        border: `1px solid ${
          isSimulatedFailure
            ? 'var(--border-color)'
            : isTrip
            ? 'rgba(239, 68, 68, 0.4)'
            : isOverSafe
            ? 'rgba(245, 158, 11, 0.4)'
            : 'var(--border-color)'
        }`,
      }}
    >
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <span
              className={`w-2 h-2 rounded-full ${
                isSimulatedFailure
                  ? 'bg-slate-600'
                  : feed.id === 'A'
                  ? 'bg-cyan-400 shadow-[0_0_8px_rgba(6,182,212,0.6)]'
                  : 'bg-indigo-400 shadow-[0_0_8px_rgba(129,140,248,0.6)]'
              }`}
              aria-hidden
            />
            <span className="font-mono text-xs font-bold text-text-primary tracking-wide">
              {feed.name}
            </span>
          </div>

          <span
            className={`font-mono text-[9px] px-1.5 py-0.5 rounded border uppercase font-bold tracking-wider ${
              isSimulatedFailure
                ? 'bg-red-950/40 text-red-400 border-red-900/60'
                : isTrip
                ? 'bg-red-950/40 text-red-300 border-red-500/60'
                : isOverSafe
                ? 'bg-amber-950/40 text-amber-300 border-amber-500/60'
                : 'bg-slate-900 text-slate-300 border-slate-700'
            }`}
          >
            {isSimulatedFailure
              ? 'ОТКАЗ'
              : isTrip
              ? 'ПЕРЕГРУЗКА'
              : isOverSafe
              ? '> 80% ЛИМИТ'
              : 'В НОРМЕ'}
          </span>
        </div>

        <div className="flex items-baseline justify-between mb-2">
          <div className="flex items-baseline gap-1">
            <span
              className="text-xl font-mono font-bold tabular-nums"
              style={{ color: barColor }}
            >
              {activeDrawW}
            </span>
            <span className="text-[10px] font-mono text-text-muted">
              Вт ({activeCurrentA}A)
            </span>
          </div>
          <span className="text-[10px] font-mono tabular-nums text-text-muted">
            {activePct}%
          </span>
        </div>

        {/* Meter Gauge Container */}
        <div className="relative mb-2">
          {/* Base bar */}
          <div
            className="w-full rounded-[2px] overflow-hidden relative"
            style={{
              height: 12,
              background: 'var(--bg-input)',
              border: '1px solid var(--border-color)',
            }}
            role="progressbar"
            aria-valuenow={activePct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${feed.name}: ${activePct}% нагрузки`}
          >
            {/* Failover Ghost Surge (visible in normal view to show potential surge) */}
            {!isSimulatedFailure && !failoverSurgeActive && feed.failoverDrawW > feed.normalDrawW && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${Math.min(failoverPct, 100)}%`,
                  background:
                    feed.failoverDrawW > capacity.safeLimitW
                      ? 'repeating-linear-gradient(45deg, rgba(245,158,11,0.2), rgba(245,158,11,0.2) 4px, rgba(245,158,11,0.35) 4px, rgba(245,158,11,0.35) 8px)'
                      : 'repeating-linear-gradient(45deg, rgba(100,116,139,0.2), rgba(100,116,139,0.2) 4px, rgba(100,116,139,0.35) 4px, rgba(100,116,139,0.35) 8px)',
                  transition: 'width 400ms ease',
                }}
                title={`Потенциальная нагрузка при отказе ${oppositeFeedName}: ${feed.failoverDrawW} Вт (${failoverPct}%)`}
              />
            )}

            {/* Active load bar */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${Math.min(activePct, 100)}%`,
                background: barColor,
                transition: 'width 400ms ease',
              }}
            />

            {/* 80% Safety Limit Line (NEC/NFPA 70 continuous threshold) */}
            <div
              style={{
                position: 'absolute',
                left: '80%',
                top: 0,
                bottom: 0,
                width: 2,
                background: 'rgba(239, 68, 68, 0.75)',
                zIndex: 10,
              }}
              title={`Порог безопасности 80% (NEC/NFPA 70): ${capacity.safeLimitW} Вт`}
            />
          </div>

          {/* Marker Labels below bar */}
          <div className="flex justify-between text-[8px] font-mono text-text-muted mt-1 select-none">
            <span>0%</span>
            <span
              className="font-bold text-amber-500/80"
              style={{ paddingLeft: '45%' }}
            >
              80% NEC ({capacity.safeLimitW}W)
            </span>
            <span>100% ({capacity.ratedPowerW}W)</span>
          </div>
        </div>
      </div>

      {/* Failover summary block */}
      <div
        className="mt-2 pt-2 border-t text-[10px] font-mono flex flex-col gap-1"
        style={{ borderColor: 'var(--border-color)' }}
      >
        <div className="flex justify-between items-center">
          <span className="text-text-muted">При отказе {oppositeFeedName}:</span>
          <span
            className={`font-bold tabular-nums ${
              feed.failoverExceedsTrip
                ? 'text-red-400'
                : feed.failoverExceedsSafe
                ? 'text-amber-400'
                : 'text-emerald-400'
            }`}
          >
            {feed.failoverDrawW} Вт ({failoverPct}%)
          </span>
        </div>

        <div className="flex justify-between items-center text-[9px] text-text-muted">
          <span>Номинал ввода:</span>
          <span>
            {capacity.ratedAmperageA}A · {capacity.voltageV}V
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────────────────────────── */
export function RackPowerTab({ equipmentList, maxPower, powerSummary }: RackPowerTabProps) {
  // Feed configuration state
  const [voltageV] = useState<number>(DEFAULT_VOLTAGE_V);
  const [selectedAmperage, setSelectedAmperage] = useState<number>(() => (maxPower > 4000 ? 32 : DEFAULT_AMPERAGE_A));
  const [simulationState, setSimulationState] = useState<'normal' | 'trip_a' | 'trip_b'>('normal');

  useEffect(() => {
    setSelectedAmperage(maxPower > 4000 ? 32 : DEFAULT_AMPERAGE_A);
  }, [maxPower]);

  // Compute power redundancy using domain logic
  const redundancy = useMemo(() => {
    return calculateRackPowerRedundancy(equipmentList, {
      amperageA: selectedAmperage,
      amperageB: selectedAmperage,
      voltageV,
      rackMaxPowerW: maxPower,
    });
  }, [equipmentList, selectedAmperage, voltageV, maxPower]);

  const { totalLoadW, feedA, feedB, status, alerts, consumers, consumerCounts } = redundancy;

  return (
    <div className="flex flex-col gap-4 text-xs font-sans select-none">

      {/* ── 1. Redundancy / SPOF Status Banner ── */}
      <div
        className={`rounded-sm p-3 border flex items-start gap-3 transition-colors ${
          status === 'critical_overload'
            ? 'bg-red-950/20 border-red-500/50'
            : status === 'spof_risk'
            ? 'bg-amber-950/20 border-amber-500/50'
            : 'bg-emerald-950/20 border-emerald-500/40'
        }`}
      >
        <div className="shrink-0 mt-0.5">
          {status === 'critical_overload' ? (
            <AlertOctagon size={16} className="text-red-400" aria-hidden />
          ) : status === 'spof_risk' ? (
            <AlertTriangle size={16} className="text-amber-400" aria-hidden />
          ) : (
            <ShieldCheck size={16} className="text-emerald-400" aria-hidden />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                status === 'critical_overload'
                  ? 'bg-red-900/40 text-red-300 border-red-700/60'
                  : status === 'spof_risk'
                  ? 'bg-amber-900/40 text-amber-300 border-amber-700/60'
                  : 'bg-emerald-900/40 text-emerald-300 border-emerald-700/60'
              }`}
            >
              {status === 'critical_overload'
                ? '[ АВАРИЯ: СБОЙ ПРИ FAILOVER ]'
                : status === 'spof_risk'
                ? '[ SPOF: РИСК КАСКАДНОГО ОТКЛЮЧЕНИЯ ]'
                : '[ 2N РЕЗЕРВИРОВАНИЕ: ОБЕСПЕЧЕНО ]'}
            </span>
          </div>

          <p className="text-[11px] leading-relaxed text-text-secondary">
            {status === 'critical_overload'
              ? 'Отказ одного из вводов приведет к превышению 100% мощности и срабатыванию автомата на резервном вводе. Требуется срочная балансировка нагрузки!'
              : status === 'spof_risk'
              ? 'При отказе одного ввода нагрузка на оставшийся превысит допустимый предел длительной нагрузки 80% (NEC/NFPA 70). Риск отключения по тепловой перегрузке.'
              : 'Оба ввода имеют достаточный запас. При отказе любого ввода оставшийся ввод гарантированно выдержит 100% критической нагрузки в пределах безопасного порога 80%.'}
          </p>

          {alerts.length > 0 && (
            <ul className="mt-2 space-y-1 text-[10px] font-mono text-text-primary">
              {alerts.map((alt, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <span className="text-status-warning shrink-0">•</span>
                  <span>{alt}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── 2. Dual Feed Power Meters (Ввод A / Ввод B) ── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap size={13} style={{ color: 'var(--status-warning)' }} aria-hidden />
            <SectionLabel>Питание стойки: Ввод A и Ввод B</SectionLabel>
          </div>

          {/* Rating switcher */}
          <div className="flex items-center gap-1 text-[9px] font-mono">
            <span className="text-text-muted">Автомат:</span>
            {[16, 32].map(amp => (
              <button
                key={amp}
                type="button"
                onClick={() => setSelectedAmperage(amp)}
                className={`px-1.5 py-0.5 rounded transition ${
                  selectedAmperage === amp
                    ? 'bg-accent text-white font-bold'
                    : 'text-text-muted hover:text-text-primary bg-bg-input border border-border'
                }`}
              >
                {amp}A
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FeedMeter
            feed={feedA}
            oppositeFeedName="Ввода B"
            isSimulatedFailure={simulationState === 'trip_a'}
            failoverSurgeActive={simulationState === 'trip_b'}
          />
          <FeedMeter
            feed={feedB}
            oppositeFeedName="Ввода A"
            isSimulatedFailure={simulationState === 'trip_b'}
            failoverSurgeActive={simulationState === 'trip_a'}
          />
        </div>
      </div>

      {/* ── 3. Failover Simulation Controls ── */}
      <div
        className="p-3 rounded-sm border flex items-center justify-between gap-3"
        style={{
          background: 'var(--bg-app)',
          borderColor: 'var(--border-color)',
        }}
      >
        <div className="flex items-center gap-2">
          <Sliders size={13} className="text-cyan-400 shrink-0" aria-hidden />
          <div>
            <div className="text-[10px] font-bold text-text-primary font-mono uppercase">
              Тест симуляции отказа ввода
            </div>
            <div className="text-[9px] text-text-muted">
              Проверьте перенос нагрузки при аварии одного из вводов
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 font-mono text-[9px]">
          <button
            type="button"
            onClick={() => setSimulationState('normal')}
            className={`px-2 py-1 rounded transition border ${
              simulationState === 'normal'
                ? 'bg-slate-700 text-white font-bold border-slate-500'
                : 'text-text-muted hover:text-text-primary border-border bg-bg-input'
            }`}
          >
            Норма (2N)
          </button>
          <button
            type="button"
            onClick={() => setSimulationState('trip_a')}
            className={`px-2 py-1 rounded transition border flex items-center gap-1 ${
              simulationState === 'trip_a'
                ? 'bg-red-950 text-red-300 font-bold border-red-500'
                : 'text-text-muted hover:text-red-400 border-border bg-bg-input'
            }`}
          >
            <PowerOff size={10} aria-hidden />
            Отказ Ввода A
          </button>
          <button
            type="button"
            onClick={() => setSimulationState('trip_b')}
            className={`px-2 py-1 rounded transition border flex items-center gap-1 ${
              simulationState === 'trip_b'
                ? 'bg-red-950 text-red-300 font-bold border-red-500'
                : 'text-text-muted hover:text-red-400 border-border bg-bg-input'
            }`}
          >
            <PowerOff size={10} aria-hidden />
            Отказ Ввода B
          </button>
        </div>
      </div>

      {/* ── 4. Total Rack Summary ── */}
      <div
        className="p-3 rounded-sm border"
        style={{
          background: 'var(--bg-app)',
          borderColor: 'var(--border-color)',
        }}
      >
        <div className="flex justify-between items-center mb-2">
          <span className="text-[10px] font-mono text-text-muted">
            Суммарное энергопотребление стойки
          </span>
          <span className="text-sm font-mono font-bold text-text-primary tabular-nums">
            {totalLoadW} Вт
          </span>
        </div>

        <div className="grid grid-cols-4 gap-2 text-[9px] font-mono text-center pt-2 border-t border-border">
          <div className="p-1 rounded bg-bg-input border border-border">
            <div className="text-text-muted">Dual PSU</div>
            <div className="font-bold text-cyan-400">{consumerCounts.dualPsu} шт</div>
          </div>
          <div className="p-1 rounded bg-bg-input border border-border">
            <div className="text-text-muted">Только Ввод A</div>
            <div className="font-bold text-amber-400">{consumerCounts.singleA} шт</div>
          </div>
          <div className="p-1 rounded bg-bg-input border border-border">
            <div className="text-text-muted">Только Ввод B</div>
            <div className="font-bold text-amber-400">{consumerCounts.singleB} шт</div>
          </div>
          <div className="p-1 rounded bg-bg-input border border-border">
            <div className="text-text-muted">Без питания</div>
            <div className="font-bold text-text-muted">{consumerCounts.unpowered} шт</div>
          </div>
        </div>
      </div>

      {/* ── 4b. PDU Outlets Occupancy ── */}
      {powerSummary?.pdus && powerSummary.pdus.length > 0 && (
        <div
          className="p-3 rounded-sm border"
          style={{
            background: 'var(--bg-app)',
            borderColor: 'var(--border-color)',
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono text-text-muted">
              Распределение розеток PDU
            </span>
            <span className="text-[10px] font-mono text-cyan-400 font-bold">
              {powerSummary.pdus.length} PDU
            </span>
          </div>
          <div className="space-y-2">
            {powerSummary.pdus.map((pdu, idx) => {
              const total = pdu.total_outlets ?? 0;
              const free = pdu.free_outlets ?? 0;
              const occupied = Math.max(0, total - free);
              const pct = total > 0 ? Math.round((occupied / total) * 100) : 0;
              const eq = equipmentList.find(e => String(e.id) === String(pdu.equipment_id));
              const pduName = eq?.name || pdu.name || `PDU #${idx + 1}`;
              return (
                <div key={pdu.equipment_id || idx} className="text-[10px] font-mono">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-text-primary font-semibold truncate max-w-[60%]" title={pduName}>
                      {pduName}
                    </span>
                    <span className="text-text-muted tabular-nums">
                      {occupied} / {total} розеток ({pct}%)
                    </span>
                  </div>
                  <div className="w-full bg-bg-input h-1.5 rounded-full overflow-hidden border border-border">
                    <div
                      className={`h-full transition-all ${pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-cyan-500'}`}
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── 5. Consumers list with Feed Badges ── */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <BarChart2 size={12} style={{ color: 'var(--text-muted)' }} aria-hidden />
          <SectionLabel>Потребители и распределение по вводам</SectionLabel>
        </div>

        {consumers.length === 0 ? (
          <p className="text-xs py-6 text-center text-text-muted">
            Нет оборудования с данными о потреблении
          </p>
        ) : (
          <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto pr-1">
            {consumers
              .sort((a, b) => b.powerW - a.powerW)
              .map(c => {
                const isDual = c.psuFeed === 'A+B';
                return (
                  <div
                    key={c.id}
                    className="flex items-center gap-2.5 px-3 py-1.5 rounded-sm border"
                    style={{
                      background: 'var(--bg-app)',
                      borderColor: 'var(--border-color)',
                    }}
                  >
                    <Server size={11} className="shrink-0 text-text-muted" aria-hidden />
                    
                    <span className="flex-1 text-[11px] truncate text-text-primary" title={c.name}>
                      {c.name}
                    </span>

                    {/* Feed allocation badge */}
                    <span
                      className={`text-[8px] font-mono font-bold px-1.5 py-0.5 rounded border uppercase shrink-0 ${
                        isDual
                          ? 'bg-cyan-950/60 text-cyan-300 border-cyan-700/60'
                          : c.psuFeed === 'A'
                          ? 'bg-amber-950/60 text-amber-300 border-amber-700/60'
                          : 'bg-indigo-950/60 text-indigo-300 border-indigo-700/60'
                      }`}
                    >
                      {isDual ? 'ВВОД A+B (2N)' : `ВВОД ${c.psuFeed} (SPOF)`}
                    </span>

                    <span className="text-[10px] font-mono tabular-nums text-text-secondary shrink-0">
                      {c.powerW} Вт
                    </span>
                  </div>
                );
              })}
          </div>
        )}
      </div>

    </div>
  );
}
